import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Phone, Mail, MapPin, Instagram } from "lucide-react";

// Icono de TikTok
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  return (
    <footer className="relative bg-[#050505] border-t border-white/10 overflow-hidden">
      {/* Animated light line */}
      <div className="absolute top-0 left-0 right-0 h-[1px] overflow-hidden">
        <motion.div
          className="h-full w-32 bg-gradient-to-r from-transparent via-[#00A8FF] to-transparent"
          animate={{ x: ["-100%", "calc(100vw + 100%)"] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12">
          {/* Logo & Description */}
          <div className="sm:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo">
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-[#00A8FF]" />
              <span className="text-xl sm:text-2xl font-black tracking-tighter uppercase">
                <span className="text-white">Brill</span>
                <span className="text-[#00A8FF]">ox</span>
              </span>
            </Link>
            <p className="text-white/60 font-light max-w-md text-sm sm:text-base">
              Transformamos tu vehículo en una obra de arte. Detailing premium con
              los mejores productos y técnicas del mercado.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-base sm:text-lg font-bold uppercase tracking-wider mb-4 sm:mb-6 text-white">
              Contacto
            </h4>
            <div className="space-y-3 sm:space-y-4">
              <a
                href="tel:+34680197056"
                className="flex items-center gap-2 sm:gap-3 text-white/60 hover:text-[#00A8FF] transition-colors group text-sm sm:text-base"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 group-hover:text-[#00A8FF] flex-shrink-0" />
                +34 680 19 70 56
              </a>
              <a
                href="mailto:brillox@brilloxcardetail.com"
                className="flex items-start gap-2 sm:gap-3 text-white/60 hover:text-[#00A8FF] transition-colors group text-xs sm:text-sm break-all"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 group-hover:text-[#00A8FF] flex-shrink-0 mt-0.5" />
                <span className="break-all">brillox@brilloxcardetail.com</span>
              </a>
              <div className="flex items-start gap-2 sm:gap-3 text-white/60 text-xs sm:text-sm" data-testid="footer-address">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0" />
                <span>
                  Pol. Ind. Juan y Antonio, Polígono Cubilete, 18, 28864 Ajalvir, Madrid
                </span>
              </div>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-base sm:text-lg font-bold uppercase tracking-wider mb-4 sm:mb-6 text-white">
              Síguenos
            </h4>
            <div className="flex gap-3 sm:gap-4">
              <motion.a
                href="https://instagram.com/brilloxcardetail"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 sm:w-12 sm:h-12 glass-card rounded-lg flex items-center justify-center text-white/60 hover:text-[#00A8FF] hover:border-[#00A8FF]/50 transition-all"
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.a>
              <motion.a
                href="https://tiktok.com/@brilloxcardetail"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 sm:w-12 sm:h-12 glass-card rounded-lg flex items-center justify-center text-white/60 hover:text-[#00A8FF] hover:border-[#00A8FF]/50 transition-all"
                data-testid="footer-social-tiktok"
              >
                <TikTokIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-xs sm:text-sm text-center sm:text-left">
            © {new Date().getFullYear()} Brillox. Todos los derechos reservados.
          </p>
          <div className="flex gap-4 sm:gap-6">
            <Link to="/servicios" className="text-white/40 hover:text-[#00A8FF] text-xs sm:text-sm transition-colors">
              Servicios
            </Link>
            <Link to="/galeria" className="text-white/40 hover:text-[#00A8FF] text-xs sm:text-sm transition-colors">
              Galería
            </Link>
            <Link to="/contacto" className="text-white/40 hover:text-[#00A8FF] text-xs sm:text-sm transition-colors">
              Contacto
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
